import { z } from 'zod';
import { IdSchema, TimestampSchema, VersionSchema } from './common.js';
import { ColorSchema } from './settings.js';
/** Business records a calendar event may point at (specification 8.13). */
export const CalendarResourceTypeSchema = z.enum([
    'job_file',
    'project',
    'lead',
    'inspection',
    'company',
    'contact',
    'customer_account',
    'property',
]);
export const CalendarEventSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    title: z.string(),
    notes: z.string().nullable(),
    startsAt: TimestampSchema,
    endsAt: TimestampSchema,
    allDay: z.boolean(),
    eventTypeId: IdSchema.nullable(),
    color: z.string().nullable(),
    assigneeMembershipId: IdSchema.nullable(),
    resourceType: CalendarResourceTypeSchema.nullable(),
    resourceId: IdSchema.nullable(),
    createdByMembershipId: IdSchema,
    archivedAt: TimestampSchema.nullable(),
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
const endsAfterStart = (value) => !value.startsAt || !value.endsAt || value.startsAt < value.endsAt;
const ORDER = { path: ['endsAt'], message: 'The event must end after it starts' };
const resourcePair = (value) => (value.resourceType === undefined && value.resourceId === undefined) ||
    (value.resourceType == null) === (value.resourceId == null);
const PAIR = { path: ['resourceId'], message: 'Give both the resource type and id, or neither' };
export const CreateCalendarEventRequestSchema = z
    .strictObject({
    title: z.string().trim().min(1).max(200),
    notes: z.string().trim().max(4000).nullable().default(null),
    startsAt: TimestampSchema,
    endsAt: TimestampSchema,
    allDay: z.boolean().default(false),
    eventTypeId: IdSchema.nullable().default(null),
    color: ColorSchema.nullable().default(null),
    assigneeMembershipId: IdSchema.nullable().default(null),
    resourceType: CalendarResourceTypeSchema.nullable().default(null),
    resourceId: IdSchema.nullable().default(null),
})
    .refine(endsAfterStart, ORDER)
    .refine(resourcePair, PAIR);
export const UpdateCalendarEventRequestSchema = z
    .strictObject({
    title: z.string().trim().min(1).max(200).optional(),
    notes: z.string().trim().max(4000).nullable().optional(),
    startsAt: TimestampSchema.optional(),
    endsAt: TimestampSchema.optional(),
    allDay: z.boolean().optional(),
    eventTypeId: IdSchema.nullable().optional(),
    color: ColorSchema.nullable().optional(),
    assigneeMembershipId: IdSchema.nullable().optional(),
    resourceType: CalendarResourceTypeSchema.nullable().optional(),
    resourceId: IdSchema.nullable().optional(),
    version: VersionSchema,
})
    .refine(endsAfterStart, ORDER)
    .refine(resourcePair, PAIR);
export const CalendarEventVersionCommandSchema = z.strictObject({ version: VersionSchema });
/** A bounded window; the server refuses ranges over 100 days. Overlap, not start-only. */
export const CalendarRangeQuerySchema = z
    .strictObject({
    from: TimestampSchema,
    to: TimestampSchema,
    eventTypeId: IdSchema.optional(),
    assigneeMembershipId: IdSchema.optional(),
    includeArchived: z.stringbool().default(false),
})
    .refine((value) => value.from < value.to, { path: ['to'], message: 'to must be after from' });
/** What a calendar view shows: events plus read-only projections of scheduled work. */
export const CalendarEntrySchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('event'), event: CalendarEventSchema }),
    z.strictObject({
        kind: z.literal('milestone'),
        id: IdSchema,
        name: z.string(),
        state: z.string(),
        jobFileId: IdSchema,
        projectId: IdSchema.nullable(),
        startsAt: TimestampSchema,
        endsAt: TimestampSchema,
        ownerMembershipId: IdSchema.nullable(),
    }),
    z.strictObject({
        kind: z.literal('inspection'),
        id: IdSchema,
        status: z.string(),
        jobFileId: IdSchema,
        inspectionTypeId: IdSchema.nullable(),
        /** Only when the caller may read properties. */
        propertyAddress: z.string().nullable(),
        startsAt: TimestampSchema,
        endsAt: TimestampSchema,
        ownerMembershipId: IdSchema.nullable(),
    }),
]);
export const CalendarSchema = z.strictObject({
    from: TimestampSchema,
    to: TimestampSchema,
    items: z.array(CalendarEntrySchema),
});
